import { keyframes } from '@emotion/react'
import { Icon, IconButton, styled, Tooltip } from 'opensolar-ui'
import { useEffect, useState } from 'react'
import { AttachedFile } from './ChatContext'

const getFileIconName = (file: File) => {
  const type = file.type.toLowerCase()
  const extension = file.name.split('.').pop()?.toLowerCase()

  if (type.startsWith('image/')) {
    return 'image' as const
  }
  if (type === 'application/pdf' || extension === 'pdf') {
    return 'picture_as_pdf' as const
  }
  if (type.includes('word') || type.includes('document') || extension === 'doc' || extension === 'docx') {
    return 'description' as const
  }
  return 'insert_drive_file' as const
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const FileIconContainer = styled('div')<{ isError: boolean }>(({ isError }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 4,
  backgroundColor: isError ? '#ffebee' : '#f5f5f5',
  border: isError ? '1px solid #f44336' : '1px solid #e0e0e0',
  cursor: 'pointer',
  marginRight: 4,
  marginBottom: 4,
}))

const ImageThumbnail = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 4,
})

const IconWrapper = styled('div')({
  color: '#666',
})

const LoadingSpinner = styled('div')({
  width: 16,
  height: 16,
  border: '2px solid #e0e0e0',
  borderTop: '2px solid #666',
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
})

const ProgressContainer = styled('div')({
  position: 'relative',
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const CircularProgress = styled('svg')({
  width: 24,
  height: 24,
  transform: 'rotate(-90deg)',
})

const ProgressText = styled('div')({
  position: 'absolute',
  fontSize: 8,
  fontWeight: 600,
  color: '#1751D0',
  lineHeight: 1,
})

const RemoveButton = styled(IconButton)({
  position: 'absolute',
  top: -8,
  right: -8,
  width: 16,
  height: 16,
  padding: 0,
  backgroundColor: 'white',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  minWidth: 16,
  minHeight: 16,
})

const TooltipContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxWidth: '280px',
})

const TooltipFileName = styled('div')({
  fontWeight: 600,
  fontSize: '13px',
  color: '#111827',
  wordBreak: 'break-word',
})

const TooltipError = styled('div')({
  fontSize: '12px',
  color: '#DC2626',
  marginTop: '4px',
})

const TooltipNote = styled('div')({
  fontSize: '11px',
  color: '#6B7280',
  marginTop: '4px',
  fontStyle: 'italic',
})

interface AttachedFileIconProps {
  attachedFile: AttachedFile
  onRemove: () => void
}

const isImageFile = (file: File) => file.type.startsWith('image/')

export const AttachedFileIcon = ({ attachedFile, onRemove }: AttachedFileIconProps) => {
  const isError = attachedFile.uploadState === 'error'
  const isUploading = attachedFile.uploadState === 'uploading'
  const isProcessing = attachedFile.uploadState === 'processing'
  const isUploaded = attachedFile.uploadState === 'uploaded'
  const progress = attachedFile.progress ?? 0
  const radius = 10
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const isImage = isImageFile(attachedFile.file)

  useEffect(() => {
    if (isImage && isUploaded && !isError) {
      const url = URL.createObjectURL(attachedFile.file)
      setImagePreviewUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setImagePreviewUrl((prevUrl) => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl)
        }
        return null
      })
    }
  }, [isImage, isUploaded, isError, attachedFile.file.name, attachedFile.file.size])

  const getTooltipContent = () => {
    if (isProcessing) {
      return 'Processing file...'
    }
    if (isUploading) {
      return `${attachedFile.file.name} (${progress}%)`
    }
    if (isError && attachedFile.error) {
      return (
        <TooltipContent>
          <TooltipFileName>{attachedFile.file.name}</TooltipFileName>
          <TooltipError>{attachedFile.error}</TooltipError>
          <TooltipNote>This file will be excluded when sending the message.</TooltipNote>
        </TooltipContent>
      )
    }
    return attachedFile.file.name
  }

  const showImageThumbnail = isImage && imagePreviewUrl && isUploaded && !isError

  return (
    <Tooltip title={getTooltipContent()} arrow>
      <FileIconContainer isError={isError}>
        {isUploading ? (
          <ProgressContainer>
            <CircularProgress>
              <circle cx="12" cy="12" r={radius} fill="none" stroke="#e0e0e0" strokeWidth="2" />
              <circle
                cx="12"
                cy="12"
                r={radius}
                fill="none"
                stroke="#1751D0"
                strokeWidth="2"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </CircularProgress>
            <ProgressText>{progress}%</ProgressText>
          </ProgressContainer>
        ) : isProcessing ? (
          <LoadingSpinner />
        ) : isError ? (
          <Icon icon="cloud_upload" size="small" color="#f44336" />
        ) : showImageThumbnail ? (
          <ImageThumbnail src={imagePreviewUrl} alt={attachedFile.file.name} />
        ) : (
          <IconWrapper>
            <Icon icon={getFileIconName(attachedFile.file)} size="small" />
          </IconWrapper>
        )}
        <RemoveButton
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label="Remove file"
        >
          <Icon icon="close" size="xSmall" />
        </RemoveButton>
      </FileIconContainer>
    </Tooltip>
  )
}
